import { realtorsApi, usersManagementApi } from 'app/services/api';
import { normalize, schema } from 'normalizr';

import { RoleAssignmentType } from 'constants/role';
import { RoleName } from 'constants/roleNames';
import { DeletedUsersPolicy, RolesFilterOperator, UserSearchField } from 'constants/userSearch';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';
import { transformRealtor, transformRealtors } from 'utils/realtor';

export const REALTORS_ACTION_TYPE = createNamedActions('realtors', {
  GET_REALTORS: 'GET_REALTORS',
  EDIT_REALTOR: 'EDIT_REALTOR',
});

const realtorsSchema = new schema.Entity('data', {}, { idAttribute: 'id' });

export const getRealtorsAction = catchAsyncActionError(
  ({ companyId, ...params }) =>
    async dispatch => {
      const [realtorsResponse, LORealtorsResponse] = await Promise.all([
        realtorsApi.getRealtors(params),
        // realtors with extended functionality
        usersManagementApi.searchUsers({
          fields: [UserSearchField.ID],
          filter: {
            companyId,
            roleNames: [RoleName.REALTOR, RoleName.EMPLOYEE],
            rolesOperator: RolesFilterOperator.AND,
            excludeSubCompanyUsers: true,
            deletedPolicy: DeletedUsersPolicy.EXCLUDE_DELETED,
          },
        }),
      ]);

      const realtors = realtorsResponse?.data;
      const LORealtors = LORealtorsResponse?.data?.items;
      const normalizedRealtors = normalize(realtors, [realtorsSchema]).entities.data;

      return dispatch({
        type: REALTORS_ACTION_TYPE.GET_REALTORS,
        payload: transformRealtors({ normalizedRealtors, LORealtors }),
      });
    },
);

export const editRealtorAction =
  ({
    companyId,
    id,
    emailAddress,
    phoneNumber,
    firstName,
    lastName,
    assignedLoanOfficerId,
    connectedLoanOfficerId,
    isLoanOfficer,
    isLoanOfficerFlagChanged,
  }) =>
  async dispatch => {
    const assignedLoanOfficerData = {};

    const roleAssigmentPayload = [
      {
        userId: id,
        roleName: RoleName.EMPLOYEE,
        type: RoleAssignmentType.VIA_NAME,
      },
    ];

    const roleAssigmentRequest = () =>
      isLoanOfficer
        ? usersManagementApi.createUserRoleAssignmentsV2(companyId, roleAssigmentPayload)
        : usersManagementApi.deleteUserRoleAssignmentsV2(companyId, roleAssigmentPayload);

    const responses = await Promise.allSettled([
      realtorsApi.assignLoanOfficer({
        id,
        assignedLoanOfficerId,
        connectedLoanOfficerId,
      }),
      usersManagementApi.editUser({
        companyId,
        id,
        emailAddress,
        phoneNumber,
        firstName,
        lastName,
      }),
      isLoanOfficerFlagChanged && roleAssigmentRequest(),
    ]);

    const [assignLoanOfficerResponse, editUserResponse] = responses;
    const assignLoanOfficerData = assignLoanOfficerResponse?.value?.data;
    const editUserData = editUserResponse?.value?.data;

    assignedLoanOfficerData.assignedLoanOfficer =
      assignLoanOfficerData?.assignedLoanOfficer || null;
    assignedLoanOfficerData.assignedLoanOfficerId =
      assignLoanOfficerData?.assignedLoanOfficerId || null;
    assignedLoanOfficerData.connectedLoanOfficer =
      assignLoanOfficerData?.connectedLoanOfficer || null;
    assignedLoanOfficerData.connectedLoanOfficerId =
      assignLoanOfficerData?.connectedLoanOfficerId || null;

    dispatch({
      type: REALTORS_ACTION_TYPE.EDIT_REALTOR,
      payload: transformRealtor({ id, ...editUserData, ...assignedLoanOfficerData, isLoanOfficer }),
    });

    responses.forEach(result => {
      if (result.status === 'rejected') {
        throw result.reason;
      }
    });
  };
